// Look-alikes puzzles. A puzzle is a region that was built by gluing N copies of one hidden piece together, so a
// solution always exists; the solver then checks how many different solutions there are and we keep puzzles in
// the sweet spot for the requested difficulty.

import { makeRng, type Rng } from "../../../shared/rng";
import {
  boundsOf, byReading, cellKey, cutsOfPartition, hasHoles, normalize, orientations, partitionKey, perimeter, regionSymmetries, type Cell
} from "./polyomino";
import { pieceMap, solveCongruent } from "./solver";

export interface CutSpec {
  cells: Cell[]; // reading order, top-left at (0,0)
  pieces: number; // N
  size: number; // cells per piece
  solution: string[]; // the cut edges of one valid answer
  solutions: number; // how many answers exist counting turned and flipped copies separately (capped at 50)
  ways: number; // how many really different answers exist, turned and flipped copies counted once
  title: string;
}

const DIRS: readonly Cell[] = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** cells per piece, number of pieces, and how many different answers are acceptable (fewer = harder) */
const LEVELS: Record<number, { k: number; n: number; minSol: number; maxSol: number }> = {
  1: { k: 3, n: 3, minSol: 1, maxSol: 6 },
  2: { k: 4, n: 3, minSol: 1, maxSol: 4 },
  3: { k: 4, n: 4, minSol: 1, maxSol: 3 },
  4: { k: 5, n: 3, minSol: 1, maxSol: 2 },
  5: { k: 5, n: 4, minSol: 1, maxSol: 1 }
};

function randomPiece(rng: Rng, k: number): Cell[] {
  for (let tries = 0; tries < 200; tries++) {
    const cells = new Map<string, Cell>([["0,0", [0, 0]]]);
    while (cells.size < k) {
      const [x, y] = rng.pick([...cells.values()]);
      const [dx, dy] = rng.pick(DIRS);
      const key = cellKey(x + dx, y + dy);
      if (!cells.has(key)) cells.set(key, [x + dx, y + dy]);
    }
    const shape = normalize([...cells.values()]);
    const b = boundsOf(shape);
    // straight bars and filled rectangles cut too easily to be interesting
    if (k >= 3 && (b.w === 1 || b.h === 1 || b.w * b.h === k)) continue;
    return shape;
  }
  return normalize([[0, 0], [0, 1], [1, 1]]); // the L-tromino
}

/** glue n turned/flipped copies of `piece` edge to edge, preferring compact blobs */
function growRegion(rng: Rng, piece: Cell[], n: number): Cell[] | null {
  const os = orientations(piece);
  const occ = new Set<string>();
  const add = (o: Cell[], tx: number, ty: number) => o.forEach(([x, y]) => occ.add(cellKey(x + tx, y + ty)));
  add(rng.pick(os), 0, 0);
  for (let id = 1; id < n; id++) {
    const cands: { o: Cell[]; tx: number; ty: number; score: number }[] = [];
    const seen = new Set<string>();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const key of occ) {
      const [x, y] = key.split(",").map(Number);
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    for (const key of occ) {
      const [cx, cy] = key.split(",").map(Number);
      for (const [dx, dy] of DIRS) {
        const qx = cx + dx;
        const qy = cy + dy;
        if (occ.has(cellKey(qx, qy))) continue;
        os.forEach((o, oi) => {
          for (const [px, py] of o) {
            const tx = qx - px;
            const ty = qy - py;
            const id2 = oi + ":" + tx + ":" + ty;
            if (seen.has(id2)) continue;
            seen.add(id2);
            let ok = true;
            for (const [x, y] of o) if (occ.has(cellKey(x + tx, y + ty))) { ok = false; break; }
            if (!ok) continue;
            let contacts = 0;
            let bx0 = minX, by0 = minY, bx1 = maxX, by1 = maxY;
            for (const [x, y] of o) {
              const gx = x + tx;
              const gy = y + ty;
              for (const [ex, ey] of DIRS) if (occ.has(cellKey(gx + ex, gy + ey))) contacts++;
              bx0 = Math.min(bx0, gx); bx1 = Math.max(bx1, gx); by0 = Math.min(by0, gy); by1 = Math.max(by1, gy);
            }
            const area = (bx1 - bx0 + 1) * (by1 - by0 + 1);
            cands.push({ o, tx, ty, score: contacts - 0.25 * area });
          }
        });
      }
    }
    if (!cands.length) return null;
    cands.sort((a, b) => b.score - a.score);
    const top = cands.slice(0, Math.max(3, Math.ceil(cands.length * 0.25)));
    const c = rng.pick(top);
    add(c.o, c.tx, c.ty);
  }
  return normalize([...occ].map((k) => k.split(",").map(Number) as unknown as Cell));
}

export function specFromRegion(cells: readonly Cell[], n: number, title: string): CutSpec | null {
  const sorted = cells.slice().sort(byReading);
  const found = solveCongruent(sorted, n, { limit: 50, budgetMs: 1500 });
  if (!found.solutions.length) return null;
  const cuts = cutsOfPartition(sorted, pieceMap(sorted, found.solutions[0]));
  const syms = regionSymmetries(sorted);
  const ways = new Set(found.solutions.map((sol) => partitionKey(sorted, sol.map((piece) => piece.map((i) => sorted[i])), syms))).size;
  return { cells: sorted, pieces: n, size: sorted.length / n, solution: cuts, solutions: found.solutions.length, ways, title };
}

/** A fresh puzzle for the given difficulty (1-5), fully determined by `seed`. */
export function generateCut(seed: string, difficulty: number): CutSpec {
  const level = LEVELS[Math.max(1, Math.min(5, difficulty))];
  const rng = makeRng("cut|" + seed);
  let fallback: CutSpec | null = null;
  for (let attempt = 0; attempt < 400; attempt++) {
    const piece = randomPiece(rng, level.k);
    const cells = growRegion(rng, piece, level.n);
    if (!cells) continue;
    const b = boundsOf(cells);
    if (cells.length === b.w * b.h || hasHoles(cells)) continue;
    // keep it chunky: stringy shapes are neither pretty nor interesting
    if (Math.min(b.w, b.h) < 3 || Math.max(b.w, b.h) > Math.ceil(Math.sqrt(cells.length)) + 2) continue;
    if (cells.length / (b.w * b.h) < 0.62 || perimeter(cells) > 2 * (b.w + b.h) + 4) continue;
    const spec = specFromRegion(cells, level.n, `Cut this shape into ${level.n} identical pieces`);
    if (!spec) continue;
    if (spec.solutions >= level.minSol && spec.solutions <= level.maxSol) return spec;
    if (!fallback || spec.solutions < fallback.solutions) fallback = spec;
  }
  return fallback ?? CLASSIC_L;
}

/** Two look-alikes: the easiest way in. The halves are turned half way round relative to each other. */
export const TWO_HALVES: CutSpec = (() => {
  const rows = ["####", "####", "##.."];
  const cells: Cell[] = [];
  rows.forEach((r, y) => [...r].forEach((c, x) => c === "#" && cells.push([x, y])));
  return specFromRegion(cells, 2, "Cut this shape into 2 identical pieces")!;
})();

/** The classic: an L that is made of four small copies of itself. */
export const CLASSIC_L: CutSpec = (() => {
  const cells: Cell[] = [];
  for (let y = 0; y < 4; y++) for (let x = 0; x < 4; x++) if (!(x >= 2 && y < 2)) cells.push([x, y]);
  return specFromRegion(cells, 4, "Cut this L into 4 identical pieces")!;
})();

/** A 5×5 square with the middle missing: 24 cells, four pieces of 6. */
export const PICTURE_FRAME: CutSpec = (() => {
  const cells: Cell[] = [];
  for (let y = 0; y < 5; y++) for (let x = 0; x < 5; x++) if (!(x === 2 && y === 2)) cells.push([x, y]);
  return specFromRegion(cells, 4, "Cut this frame into 4 identical pieces")!;
})();
